import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Input,
  Row,
  Col,
  Badge,
  Button,
} from "reactstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";

const ProductTrainingDetails = () => {
  document.title = "Product Training Details | ArcMedix";
  const [training, setTraining] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const { formData } = location.state || {};
  const navigate = useNavigate();

  // Modals state
  const [imageViewerModal, setImageViewerModal] = useState(false);
  const [documentViewerModal, setDocumentViewerModal] = useState(false);
  const [currentMedia, setCurrentMedia] = useState([]);
  const [mediaType, setMediaType] = useState(''); // 'image' or 'document'

  const openMediaViewer = (media, type) => {
    setCurrentMedia(media || []);
    setMediaType(type);
    if (type === 'image') {
      setImageViewerModal(true);
    } else {
      setDocumentViewerModal(true);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (formData) {
      setTraining([formData]); // Wrap formData in array since we expect an array of items
    }
  }, [formData]);

  const renderMediaPreview = (media, type) => {
    if (!media || media.length === 0 || media[0] === "") {
      return (
        <Button className="btn btn-soft-success waves-effect waves-light">
          View
        </Button>
      );
    }
    return (
      <Button className="btn btn-soft-success waves-effect waves-light">
        View ({media.length})
      </Button>
    );
  };

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
            <Button 
            color="light" 
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            <FeatherIcon icon="arrow-left" className="me-1" />
            Back
          </Button>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder="Search Product Training Details..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">Product Training Details</h3>
        </CardHeader>

        <CardBody>
          <div
            className="table-responsive"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: "0.1rem",
                borderCollapse: "separate",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>#</th>
                  <th>Asset Name</th>
                  <th>Serial No.</th>
                  <th>Training Mode</th>
                  <th>No. of Trainees</th>
                  <th>Department</th>
                  <th>Preferred Schedule</th>
                  <th>Preferred Language</th>
                  <th>Prior Training</th>
                  <th>Images</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {training?.map((item, ind) => (
                    <motion.tr
                      key={item?.id || ind}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>{item?.asset?.assetName || "N/A"}</td>
                      <td>{item?.asset?.serialNumber || "N/A"}</td>
                      <td>{item?.trainingMode || "N/A"}</td>
                      <td>{item?.numberOfTrainees || "N/A"}</td>
                      <td>{item?.departmentsInvolved || "N/A"}</td>
                      <td>{item?.preferredSchedule || "N/A"}</td>
                      <td>{item?.languagePreference || "N/A"}</td>
                      <td>
                        <Badge
                          color={item?.priorTraining ? "success" : "warning"}
                        >
                          {item?.priorTraining ? "Yes" : "No"}
                        </Badge>
                      </td>
                      <td
                        className="items-center"
                        onClick={() => openMediaViewer(item?.images, "image")}
                        style={{ cursor: "pointer" }}
                      >
                        {renderMediaPreview(item?.images, "image")}
                      </td>
                      <td
                        className="items-center"
                        onClick={() => openMediaViewer(item?.documents, "document")}
                        style={{ cursor: "pointer" }}
                      >
                        {renderMediaPreview(item?.documents, "document")}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {training.length === 0 && (
              <div className="text-center py-5 text-muted">
                No Product Training Details found
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Image Viewer Modal */}
      <Modal
        isOpen={imageViewerModal}
        toggle={() => setImageViewerModal(false)}
        size="lg"
      >
        <div className="modal-header">
          <h5 className="modal-title">Training Images</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setImageViewerModal(false)}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          {currentMedia.length > 0 && currentMedia[0] !== "" ? (
            <Row>
              {currentMedia.map((mediaUrl, index) => (
                <Col md={6} className="mb-4" key={index}>
                  <div className="card border-0 shadow-sm">
                    <div className="position-relative">
                      <img
                        src={mediaUrl}
                        alt={`Training Image ${index + 1}`}
                        className="img-fluid rounded-top"
                        style={{
                          objectFit: "cover",
                          height: "200px",
                          width: "100%",
                        }}
                      />
                    </div>
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          Image {index + 1}
                        </small>
                        <a
                          href={mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View Full
                        </a>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-4">
              <FeatherIcon icon="image" size="48" className="text-muted mb-2" />
              <p className="text-muted">No images available</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setImageViewerModal(false)}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        isOpen={documentViewerModal}
        toggle={() => setDocumentViewerModal(false)}
        size="lg"
      >
        <div className="modal-header">
          <h5 className="modal-title">Training Documents</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDocumentViewerModal(false)}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          {currentMedia.length > 0 && currentMedia[0] !== "" ? (
            <Row>
              {currentMedia.map((docUrl, index) => {
                const isPdf = docUrl?.toLowerCase().endsWith(".pdf");
                const isImage = ["jpg", "jpeg", "png", "gif"].some(ext =>
                  docUrl?.toLowerCase().endsWith(ext)
                );
                
                return (
                  <Col md={6} className="mb-4" key={index}>
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <FeatherIcon
                          icon={isPdf ? "file-text" : isImage ? "image" : "file"}
                          size="48"
                          className="text-primary mb-3"
                        />
                        <h6 className="mb-2">Document {index + 1}</h6>
                        <small className="text-muted d-block mb-3 text-truncate">
                          {docUrl?.split("/").pop()}
                        </small>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          {isPdf ? "View PDF" : isImage ? "View Image" : "Download"}
                        </a>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-4">
              <FeatherIcon icon="file" size="48" className="text-muted mb-2" />
              <p className="text-muted">No documents available</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDocumentViewerModal(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProductTrainingDetails;